import { useDispatch, useSelector } from 'react-redux'
import { useCallback } from 'react'
import ReleaseActions from 'reducers/Releases'
import ModalActions from 'reducers/Modal'
import PlaylistActions, { thunks as PlaylistThunks } from 'reducers/PlayList'
import { thunks as FlaggedThunks } from 'reducers/Flagged'
import { thunks as ReleaseThunks } from 'reducers/Releases'
import NotificationActions from 'reducers/Notification'
import { NotificationType } from 'constants/enums'
import { ReleasesService } from 'services'
import { trackEvent } from 'utils/segmentService'
import { normalizeValue } from 'utils/analytics'
import useArchiveToFilter from 'utils/hooks/useArchiveToFilter'

const useReleaseActions = (
  release,
  setRelease,
  eventSource = 'release_information'
) => {
  const dispatch = useDispatch()
  const { enabled: archiveToFilterEnabled } = useArchiveToFilter()
  const { playlistAbstracts } = useSelector((state) => state.playList)
  const syncReleaseState = useCallback(
    (updatedRelease) => {
      setRelease(updatedRelease)
      dispatch(ReleaseActions.setCurrentReleaseInformation(updatedRelease))
      dispatch(ReleaseActions.updateRelease(updatedRelease))
    },
    [dispatch, setRelease]
  )

  // Flag actions
  const handleFlag = useCallback(() => {
    const flagged = release?.flagged
    const nextFlagged = !flagged
    const payload = {
      release_id: normalizeValue(release?.releaseid),
      artist: normalizeValue(release?.artist),
      title: normalizeValue(release?.title),
      action: nextFlagged ? 'flag' : 'unflag',
      source: eventSource,
      type: 'release_flag'
    }
    dispatch(
      FlaggedThunks.flag(
        release.releaseid,
        nextFlagged,
        () => {
          const updatedRelease = {
            ...release,
            flagged: nextFlagged,
            songs: release.songs?.map((song) => ({
              ...song,
              flagged: nextFlagged
            }))
          }
          syncReleaseState(updatedRelease)
          dispatch(
            NotificationActions.broadcastMessage(
              `${release.artist} - ${release.title}`,
              nextFlagged ? NotificationType.flag : NotificationType.unFlag
            )
          )
          trackEvent('Release Flag', payload)
        },
        () => {}
      )
    )
  }, [dispatch, eventSource, release, syncReleaseState])

  // Archive actions
  const handleArchive = useCallback(() => {
    const currentValue = release.archived
    const currentFlagged = release.flagged

    const archiveSuccess = () => {
      const shouldUnflagOnArchive = !currentValue && currentFlagged
      const updatedRelease = {
        ...release,
        archived: !currentValue,
        flagged: shouldUnflagOnArchive ? false : release.flagged,
        songs: shouldUnflagOnArchive
          ? release.songs?.map((song) => ({
              ...song,
              flagged: false
            }))
          : release.songs
      }
      syncReleaseState(updatedRelease)

      dispatch(
        NotificationActions.broadcastMessage(
          `${release.artist} - ${release.title}`,
          currentValue ? NotificationType.unArchive : NotificationType.archive
        )
      )

      if (shouldUnflagOnArchive) {
        ReleasesService.unFlag(release.releaseid)
        dispatch(
          NotificationActions.broadcastMessage(
            `${release.artist} - ${release.title}`,
            NotificationType.unFlag
          )
        )
      }

      trackEvent('Release Archive', {
        release_id: normalizeValue(release?.releaseid),
        artist: normalizeValue(release?.artist),
        title: normalizeValue(release?.title),
        action: currentValue ? 'unarchive' : 'archive',
        unflagged_on_archive: normalizeValue(
          currentFlagged && !currentValue ? 'true' : 'false'
        ),
        source: eventSource,
        type: 'release_archive'
      })
    }

    dispatch(
      ReleaseThunks.archive(
        release.releaseid,
        !currentValue,
        archiveSuccess,
        null,
        archiveToFilterEnabled
      )
    )
  }, [dispatch, eventSource, release, syncReleaseState, archiveToFilterEnabled])

  // Playlist actions
  const getTrackIds = useCallback(() => {
    if (release) {
      return release.songs
        .filter((s) => s.assettype === 'audio')
        .map((item) => item.trackid)
    }
    return []
  }, [release])

  const getTrackNames = useCallback(() => {
    if (release) {
      return `${release.artist} - ${release.title}`
    }
    return ''
  }, [release])

  const handleAddToPlaylist = useCallback(
    async (playlist) => {
      if (archiveToFilterEnabled && release?.archived) return
      try {
        const onSuccess = () => {
          const notificationMessage = {
            songTitle: getTrackNames(),
            playlistTitle: playlist.name
          }

          dispatch(
            NotificationActions.broadcastMessage(
              notificationMessage,
              NotificationType.addedToPlaylist
            )
          )
        }

        dispatch(
          PlaylistThunks.addTracks(
            playlist.playlistid,
            getTrackIds(),
            onSuccess
          )
        )
      } catch (error) {
        console.warn(error)
      }
    },
    [
      dispatch,
      getTrackIds,
      getTrackNames,
      archiveToFilterEnabled,
      release?.archived
    ]
  )

  const createNewPlaylist = useCallback(() => {
    if (archiveToFilterEnabled && release?.archived) return
    dispatch(PlaylistActions.setSongsToAdd(getTrackIds()))
    dispatch(ModalActions.togglePlaylistModal())
  }, [dispatch, getTrackIds, archiveToFilterEnabled, release?.archived])

  return {
    // Flag
    handleFlag,

    // Archive
    handleArchive,

    // Playlist
    playlistAbstracts,
    handleAddToPlaylist,
    createNewPlaylist,

    // Helper functions
    getTrackIds,
    getTrackNames
  }
}

export default useReleaseActions
